import { isEmpty } from 'lodash-es';
import { useOfferLetterTemplates } from 'modules/offerLetter/components/OfferLetterForm/hooks/useOfferLetterTemplates';
import {
    defaultOfferLetterValues,
    IOfferLetterFormValues,
    IOfferLetterUser,
} from 'modules/offerLetter/components/OfferLetterForm/model';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ConfirmationDialog from 'shared/components/modals/ConfirmationDialog';
import { IUserInfo } from 'shared/models/User';
import {
    selectClientApprovalLevels,
} from 'store/entities/clients/selectors/timeAndPaySelectors';
import { CustomFieldType } from 'store/entities/customFields/model';
import {
    selectCustomFieldDepartmentId,
    selectCustomFieldValuesByIds,
} from 'store/entities/customFields/selectors';
import { IUserLookupRequest } from 'store/entities/users/model';
import { IOfferLetter } from '../../store/model';
import { OfferLetterMultiStepForm } from './OfferLetterMultiStepForm';

export interface ILookupFormWrapperProps {
    onSubmit?: (offer: IOfferLetterFormValues) => void;
    onCancel: () => void;
    selectedUser?: IOfferLetterUser;
    lookupData?: IUserLookupRequest;
    offer?: IOfferLetter;
    managers?: (IUserInfo | null)[];
    confirmMessage: (offer: IOfferLetterFormValues | null) => string;
    isLoading?: boolean;
}

const OfferLetterFormWrapper = ({
    onSubmit,
    onCancel,
    selectedUser,
    lookupData,
    managers,
    offer,
    confirmMessage,
    isLoading = false,
}: ILookupFormWrapperProps) => {
    const [creatingOfferData, setConfirmOfferData] = useState<IOfferLetterFormValues | null>(null);

    const templates = useOfferLetterTemplates(offer);
    const approversCount = useSelector(selectClientApprovalLevels);
    const customFieldValuesByIds = useSelector(selectCustomFieldValuesByIds);
    const departmentFieldId = useSelector(selectCustomFieldDepartmentId);
    const initialValues: IOfferLetterFormValues = getOfferLetterFormInitialValues(
        approversCount,
        offer,
        selectedUser,
        managers,
        lookupData,
        templates,
        customFieldValuesByIds,
        departmentFieldId,
    );

    const onSubmitForm = (values: IOfferLetterFormValues) => {
        setConfirmOfferData(values);
    };

    const onCancelConfirmation = () => {
        setConfirmOfferData(null);
    };

    const onConfirm = () => {
        if (creatingOfferData) {
            if (onSubmit) {
                onSubmit(creatingOfferData);
            }
            onCancelConfirmation();
        }
    };

    return (
        <>
            <OfferLetterMultiStepForm
                onCancel={onCancel}
                initialValues={initialValues}
                onSubmit={onSubmitForm}
                selectedEmployee={selectedUser}
                offer={offer}
                isSubmitting={isLoading}
            />
            <ConfirmationDialog
                open={Boolean(creatingOfferData)}
                onConfirm={onConfirm}
                onCancel={onCancelConfirmation}
            >
                {confirmMessage(creatingOfferData)}
            </ConfirmationDialog>
        </>
    );
};

export default OfferLetterFormWrapper;

function getOfferLetterFormInitialValues(
    approversCount: number,
    offer?: IOfferLetter,
    selectedUser?: IOfferLetterUser,
    managers?: (IUserInfo | null)[],
    lookupData?: IUserLookupRequest,
    templates: Partial<IOfferLetterFormValues> = {},
    customFieldValuesByIds: Record<string, any> = {},
    departmentFieldId?: string,
): IOfferLetterFormValues {
    let baseValues: IOfferLetterFormValues = {
        ...defaultOfferLetterValues,
        approvers: new Array(approversCount).fill(null),
        ...selectedUser ? {
            userId: selectedUser.id,
            first_name: selectedUser.first_name,
            last_name: selectedUser.last_name,
            email: selectedUser.email,
            phone: selectedUser.cell_phone || '',
        } : {},
        ...offer ? {
            departmentValueId: offer.custom_field_value_ids?.length
                ? offer.custom_field_value_ids.find(cfv_id =>
                    customFieldValuesByIds[cfv_id]?.custom_field_id === departmentFieldId,
                ) || null
                : null,
            jobOrderId: offer.custom_field_value_ids?.length
                ? offer.custom_field_value_ids.find(cfv_id =>
                    customFieldValuesByIds[cfv_id]?.data?.headway_connect_field === CustomFieldType.AvionteJobOrder,
                ) || null
                : null,
            template: offer.offer_letter_template_id || '',
            employeeType: offer.employment_type || null,
            payType: offer.pay_type || null,
            payRateType: offer.pay_rate_type || null,
            hours: offer.range_of_hours || null,
            startDate: offer.start_date,
            approvers: managers || [],
            employee_number: offer.employee_number,
            ...templates,
        } : {},
    };
    if (!isEmpty(lookupData) && !selectedUser) {
        baseValues = {
            ...baseValues,
            ...lookupData ? {
                first_name: lookupData.firstName || '',
                last_name: lookupData.lastName || '',
                email: lookupData.email || '',
                phone: lookupData.phone || '',
            } : {},
        };
    }
    return baseValues;
}
